import Button from '@ui/buttons/Button'
import { colors } from '@ui/colors'
import { ModalBottom } from '@ui/components/Modal'
import Typography from '@ui/typography'
import { getTokens } from 'api/ApiProvider'
import AppConfig from 'config'
import { useRootNavigationState } from 'expo-router'
import useAppActivity from 'hooks/activity/useAppActivity'
import useIdleLogoutEnabled from 'hooks/activity/useIdleLogoutEnabled'
import useIntroStore from 'hooks/useIntroStore'
import { useLogout } from 'hooks/useLogout'
import useResetNav from 'hooks/useResetNav'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FlatList, View } from 'react-native'
import Animated, {
  SharedValue,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'

const getTimeEnd = (seconds: number) => Date.now() + (seconds + 1) * 1000

const EL_HEIGHT = 128

const CounterItem = ({
  item,
  animation,
}: {
  item: number
  animation: SharedValue<number>
}) => {
  const style = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        animation.value,
        [item - 1, item, item + 1],
        [0, 1, 0]
      ),
      // transform: [
      //   {
      //     scale: interpolate(
      //       animation.value,
      //       [item - 0.5, item, item + 0.5],
      //       [0.2, 1, 0.2]
      //     ),
      //   },
      // ],
    }
  })
  return (
    <Animated.View
      style={[{ height: EL_HEIGHT }, style]}
      className=" items-center justify-center"
    >
      <Typography variant="H5" className="text-[128px]">
        {item}
      </Typography>
    </Animated.View>
  )
}

export const CountdownModal = ({
  open,
  onClose,
  timeoutSeconds,
}: {
  open: boolean
  onClose: () => void
  timeoutSeconds: number
}) => {
  const { t } = useTranslation(['common', 'app'])
  const text = 'app:inactive'

  const list = useRef<FlatList>(null)
  const animation = useSharedValue(0)

  const scrollHandler = useAnimatedScrollHandler((event) => {
    animation.value = event.contentOffset.y / EL_HEIGHT
  })

  const reset = useResetNav()
  const state = useRootNavigationState()
  const resetIntro = useIntroStore((state) => state.reset)

  const logout = useLogout()

  const timeEnd = useRef(getTimeEnd(timeoutSeconds))
  const timeLeft = useRef<number>(timeoutSeconds)

  const handleLogout = () => {
    resetIntro()
    logout()
    onClose()
    if (currentRouteName === 'index') {
      return
    }
    reset('index')
  }

  useEffect(() => {
    if (!open) {
      return
    }
    timeEnd.current = getTimeEnd(timeoutSeconds)

    const timer = setInterval(() => {
      const newTimeLeft = Math.floor((timeEnd.current - Date.now()) / 1000)
      if (newTimeLeft === timeLeft.current) {
        return
      }
      timeLeft.current = newTimeLeft

      if (newTimeLeft <= 0) {
        handleLogout()
      }

      list.current?.scrollToIndex({
        index: Math.max(newTimeLeft, 0),
        animated: true,
      })
    }, 100)

    return () => clearTimeout(timer)
  }, [open])

  const currentRouteName = state?.routes[0]?.name

  const nums = useMemo(
    () =>
      Array.from(
        { length: AppConfig.activity.logoutTimeoutSeconds + 1 },
        (_, i) => i
      ),
    []
  )

  return (
    <ModalBottom
      center
      open={open}
      close={onClose}
      title={t(`${text}.header.title`)}
      desc={t(`${text}.header.desc`)}
      body={
        <>
          <View className="items-center gap-16 rounded-xl bg-white px-16 py-8">
            <View className="flex-1 items-center gap-2">
              <Animated.FlatList
                ref={list}
                onScroll={scrollHandler}
                initialScrollIndex={AppConfig.activity.logoutTimeoutSeconds}
                style={{
                  height: EL_HEIGHT,
                }}
                showsVerticalScrollIndicator={false}
                pagingEnabled
                // scrollEnabled={false}
                inverted
                getItemLayout={(_, index) => ({
                  length: EL_HEIGHT,
                  offset: EL_HEIGHT * index,
                  index,
                })}
                renderItem={({ item }) => (
                  <CounterItem item={item} animation={animation} />
                )}
                data={nums}
              />

              <Typography variant="MDB">{t('common:seconds')}</Typography>
            </View>
          </View>
        </>
      }
      buttons={
        <View className="flex w-full flex-row items-center gap-16 self-center">
          <View className="flex flex-1 items-end">
            <Button
              variant="outlined"
              label={t('ui:stay')}
              iconStart="arrow-left"
              onPress={onClose}
            />
          </View>
          <View className="flex flex-1 items-start">
            <Button
              label={t('ui:logout')}
              onPress={handleLogout}
              variant="outlined"
              className="border-red-600"
              overrideColorFront={colors.red[600]}
              iconEnd="logout"
            />
          </View>
        </View>
      }
    ></ModalBottom>
  )
}

const Inactive = () => {
  const isUserActive = useAppActivity((s) => s.active)
  const enabled = useIdleLogoutEnabled()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (isUserActive || !enabled) {
      return
    }

    setVisible(true)
  }, [isUserActive, enabled])

  const onClose = useCallback(() => {
    // This will check if we want to refresh the tokens
    getTokens()
    setVisible(false)
  }, [])

  return (
    <CountdownModal
      open={visible}
      onClose={onClose}
      timeoutSeconds={AppConfig.activity.logoutTimeoutSeconds}
    />
  )
}

export default Inactive
